import axios from 'axios'
import Web3, { AbiFragment } from 'web3'
import {
  Address,
  GasEstimator,
  Transaction,
  TokenTransfer,
  TransferTransactionsFactory,
} from '@multiversx/sdk-core'
import { refreshAccount } from '@multiversx/sdk-dapp/utils/account'
import { sendTransactions } from '@multiversx/sdk-dapp/services'
import { toast } from 'react-toastify'

import { chainsDataType, networkType, networksData } from '../../../util/chainsData'
import { intToHex, strToHex } from '../../../util/dataDecoders'
import { chainType, tokenSelectObjectType } from '../../../util/types'
import WalletConnectAdapter from '../../../BlockchainAdapter/WalletConnectAdapter'
import MetamaskAdapter from '../../../BlockchainAdapter/MetamaskAdapter'
import { txType } from '../../../pages/AirDrop/types'
import { checkTransaction } from '../../../pages/AirDrop/actions/processTransactions'
import { updateTransactions } from '../../../contexts/airdropActionCreators'
import erc20ABI from 'src/abis/erc20ABI'

const walletConnectAdapter = new WalletConnectAdapter()
const metamaskAdapter = new MetamaskAdapter()

export const getEvmTokenBalance = async (
  contractAddress: string,
  initiator: string,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  const web3 = new Web3(
    new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
  )
  incrementRpcCount()
  const erc20Contract = new web3.eth.Contract(
    networksData[chain].abi as AbiFragment[],
    contractAddress,
  )

  const tokenDecimals = await getEvmTokenDecimals(
    contractAddress,
    chain,
    network,
    rpcRequestCount,
    incrementRpcCount,
  )
  try {
    const balance: any = await erc20Contract.methods.balanceOf().call()
    return Number(balance / BigInt(10 ** tokenDecimals))
  } catch (err) {
    console.error('Error in getting balance', err)
  }
  return 0
}

export const getEvmTokenDecimals = async (
  contractAddress: string,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  const web3 = new Web3(
    new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
  )
  incrementRpcCount()
  const erc20Contract = new web3.eth.Contract(
    networksData[chain].abi as AbiFragment[],
    contractAddress,
  )

  try {
    const decimals = await erc20Contract.methods.decimals().call()
    return Number(decimals)
  } catch (err) {
    console.error('Error in getting balance', err)
  }

  return 0
}

export const executeSelectedTransactions = async (
  transactions: any[],
  signer: any,
  initiator: string,
  selectedToken: tokenSelectObjectType,
  externalWallet: boolean,
  chain: keyof chainsDataType,
  network: networkType,
  isWalletConnected: boolean,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  try {
    switch (chain.toLowerCase()) {
      case 'ethereum':
      case 'polygon':
      case 'bnb':
        externalWallet
          ? await broadcastEVMTransactions(
              transactions,
              initiator,
              selectedToken,
              isWalletConnected,
              chain,
              network,
              rpcRequestCount,
              incrementRpcCount,
            )
          : signAndBroadcastEVMTransactions(
              transactions,
              signer,
              initiator,
              chain,
              selectedToken,
              network,
              rpcRequestCount,
              incrementRpcCount,
            )
        break
      case 'multiversx':
        externalWallet
          ? await broadcastMVXTransactions(transactions, selectedToken)
          : await signAndBroadcastMVXTransactions(
              transactions,
              signer,
              initiator,
              selectedToken,
              network,
            )
        break
    }
  } catch (error) {
    console.error(error)
    return error
  }
}

export const signAndBroadcastMVXTransactions = async (
  transactions: txType[],
  signer: any,
  initiator: string,
  selectedToken: tokenSelectObjectType,
  network: networkType,
) => {
  const factory = new TransferTransactionsFactory(new GasEstimator())
  let nonce = await recallAccountNonce(initiator)

  let txCount = 1

  for (const tx of transactions) {
    const { amount } = tx
    let transaction
    if (selectedToken?.name === networksData?.multiversx[network as networkType]?.nativeCurrency) {
      const transfer = TokenTransfer.egldFromAmount(amount.toString())

      transaction = factory.createEGLDTransfer({
        nonce,
        sender: new Address(initiator),
        receiver: new Address(tx.receiver),
        chainID: networksData.multiversx[network].chainId,
        value: transfer,
      })
    } else {
      const transfer = TokenTransfer.fungibleFromAmount(
        selectedToken?.tokenIdentifier,
        amount,
        selectedToken?.decimals,
      )

      transaction = factory.createESDTTransfer({
        tokenTransfer: transfer,
        sender: new Address(initiator),
        receiver: new Address(tx.receiver),
        nonce,
        chainID: networksData.multiversx[network].chainId,
      })
    }

    const serializedTx = transaction.serializeForSigning()
    const signature = await signer.sign(serializedTx)
    transaction.applySignature(signature)

    const txHash = await broadcastTransaction(transaction)
    nonce = nonce + 1
    tx.tx = txHash
    tx.status = 'pending'

    if (transactions.length >= 10 && txCount % 10 === 0) {
      const newStatusTransactions = transactions.slice(txCount - 10, txCount)
      updateTransactions(newStatusTransactions)
    }
    txCount += 1
  }

  //Update the last transactions that are fewer than 10
  if (transactions.length % 10 !== 0) {
    const newStatusTransactions = transactions.slice(-(transactions.length % 10))
    updateTransactions(newStatusTransactions)
  }
}

async function recallAccountNonce(address: any): Promise<number> {
  const multiversxApi =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://api.multiversx.com	'
      : 'https://devnet-api.multiversx.com'
  const url = `${multiversxApi}/accounts/${address.toString()}`
  const response = await axios.get(url)
  return response.data.nonce
}

async function broadcastTransaction(transaction: Transaction): Promise<string> {
  const multiversxApi =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://api.multiversx.com	'
      : 'https://devnet-api.multiversx.com'

  const url = `${multiversxApi}/transactions`
  const data = transaction.toSendable()

  const response = await axios.post(url, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  return response.data?.txHash
}
export const getEvmGasEstimate = async (
  transactions: any[],
  chain: keyof chainsDataType,
  network: networkType,
  token: tokenSelectObjectType,
  initiator: string,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  const web3 = new Web3(
    new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
  )

  try {
    let gas = 0
    const gasPrice = await web3.eth.getGasPrice()
    incrementRpcCount()

    if (token?.tokenIdentifier) {
      // @ts-ignore
      const erc20Contract = new web3.eth.Contract(
        // @ts-ignore
        networksData[chain].abi,
        token?.tokenIdentifier,
      )

      // for (const tx of transactions) {
      const dataString = erc20Contract.methods
        .transfer(
          // @ts-ignore
          transactions[0].receiver,
          (transactions[0].amount * 10 ** token?.decimals).toString(),
        )
        .encodeABI()
      const gas1 = Number(
        // @ts-ignore
        await web3.eth.estimateGas({
          to: transactions[0].receiver,
          data: dataString,
        }),
      )
      incrementRpcCount()
      gas = gas1 * transactions.length
      // }
    } else {
      // for (const tx of transactions) {
      const gas1 = Number(
        // @ts-ignore
        await web3.eth.estimateGas({
          from: initiator,
          to: transactions[0].receiver,
          value: transactions[0].amount * 10 ** 18,
        }),
      )
      incrementRpcCount()
      gas = gas1 * transactions.length
      // }
    }
    const calculatedGas = Number(BigInt(gas) * gasPrice) / 10 ** 9
    return calculatedGas / 1000000000
  } catch (error) {
    console.error(error)
    incrementRpcCount()
    return 0
  }
}

const signAndBroadcastEVMTransactions = async (
  transactions: txType[],
  signer: any,
  initiator: string,
  chain: keyof chainsDataType,
  selectedToken: tokenSelectObjectType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  const web3 = new Web3(
    new Web3.providers.HttpProvider(networksData[chain].testnet.rpc[rpcRequestCount]),
  )

  web3.eth.transactionPollingInterval = 20000

  const gasPrice = await web3.eth.getGasPrice()
  incrementRpcCount()
  let nonce = await web3.eth.getTransactionCount(initiator)
  incrementRpcCount()
  rpcRequestCount = rpcRequestCount >= 2 ? 0 : rpcRequestCount + 1

  let txCount = 1
  let erc20Contract

  if (selectedToken?.tokenIdentifier) {
    // @ts-ignore
    erc20Contract = new web3.eth.Contract(
      // @ts-ignore
      networksData[chain].abi,
      selectedToken?.tokenIdentifier,
    )
  }

  for (const tx of transactions) {
    let transaction

    if (selectedToken?.tokenIdentifier) {
      transaction = {
        nonce,
        from: initiator,
        to: selectedToken?.tokenIdentifier,
        value: '0',
        chainId: networksData[chain][network].chainId,
        gasPrice,
      }
      // @ts-ignore
      transaction.data = erc20Contract.methods
        // @ts-ignore
        .transfer(
          // @ts-ignore
          tx.receiver,
          (tx.amount * 10 ** selectedToken.decimals).toString(),
        )
        .encodeABI()
    } else {
      transaction = {
        nonce,
        from: initiator,
        to: tx.receiver,
        value: tx.amount * 10 ** 18,
        chainId: networksData[chain][network].chainId,
        gasPrice,
      }
    }
    let signedTx
    try {
      const web3 = new Web3(
        new Web3.providers.HttpProvider(networksData[chain].testnet.rpc[rpcRequestCount]),
      )

      web3.eth.transactionPollingInterval = 20000
      const account = web3.eth.accounts.privateKeyToAccount('0x' + signer)

      signedTx = await account.signTransaction(transaction)
      incrementRpcCount()
      rpcRequestCount = rpcRequestCount >= 2 ? 0 : rpcRequestCount + 1

      tx.status = 'pending'
      // @ts-ignore
      tx.tx = signedTx?.transactionHash?.toString()
      // @ts-ignore
      web3.eth.sendSignedTransaction(signedTx.rawTransaction)
      incrementRpcCount()
      rpcRequestCount = rpcRequestCount >= 2 ? 0 : rpcRequestCount + 1

      nonce = BigInt(Number(nonce) + 1)

      if (transactions.length >= 10 && txCount % 10 === 0) {
        const newStatusTransactions = transactions.slice(txCount - 10, txCount)
        updateTransactions(newStatusTransactions)
      }
      txCount += 1
    } catch (error) {
      toast.error('Transaction failed')
      rpcRequestCount = rpcRequestCount >= 2 ? 0 : rpcRequestCount + 1
    }
  }

  //Update the last transactions that are fewer than 10
  if (transactions.length % 10 !== 0) {
    const newStatusTransactions = transactions.slice(-(transactions.length % 10))
    updateTransactions(newStatusTransactions)
  }
}

export const broadcastMVXTransactions = async (
  transactions: txType[],
  token: tokenSelectObjectType,
) => {
  const mvxTransactions = []
  for (const tx of transactions) {
    const parsedAmount = Number(BigInt(10 ** token?.decimals) * BigInt(tx.amount))
    mvxTransactions.push({
      value: 0,
      data: `ESDTTransfer@${strToHex(token?.tokenIdentifier)}@${intToHex(parsedAmount)}`,
      gasLimit: 20000000,
      receiver: tx.receiver,
    })
  }
  await refreshAccount()

  const transactionRes = await sendTransactions({
    transactions: mvxTransactions,
    transactionsDisplayInfo: {
      processingMessage: 'Processing Airdrop transactions',
      errorMessage: 'An error has occurred during airdrop transactions. Your funds are safe.',
      successMessage: 'Airdrop transaction successful',
    },
    redirectAfterSign: false,
  })

  for (const tx of transactions) {
    tx.status = 'pending'
  }

  sessionStorage.setItem(
    'airdropMvxRes',
    JSON.stringify({
      sessionId: transactionRes?.sessionId,
      transactions,
    }),
  )

  return transactionRes
}

export const broadcastEVMTransactions = async (
  transactions: txType[],
  initiator: string,
  token: tokenSelectObjectType,
  isWalletConnected: boolean,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  if (isWalletConnected) {
    if (
      !walletConnectAdapter.checkChainMatch(networksData?.[chain]?.[network]?.chainId as number)
    ) {
      return toast.error(
        'WalletConnect is connected to a different chain. Please switch your wallet provider to the same chain as the airdrop.',
      )
    }
  } else {
    const doesChainMatch = await metamaskAdapter.checkChainMatch(
      chain.toUpperCase() as chainType,
      network,
    )
    if (!doesChainMatch) {
      return toast.error(
        'Metamask is connected to a different chain. Please switch your wallet provider to the same chain as the airdrop.',
      )
    }
  }

  let txCount = 1
  for (const tx of transactions) {
    let data = ''

    if (token.tokenIdentifier) {
      const web3 = new Web3(
        new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
      )
      incrementRpcCount()

      const erc20Contract = new web3.eth.Contract(
        // @ts-ignore
        erc20ABI,
        token?.tokenIdentifier,
      )

      data = erc20Contract.methods
        // @ts-ignore
        .transfer(
          // @ts-ignore
          tx.receiver,
          (tx.amount * 10 ** token.decimals).toString(),
        )
        .encodeABI()
    }

    let txResponse

    if (isWalletConnected) {
      txResponse = await walletConnectAdapter.sendTransaction({
        chainId: networksData?.[chain]?.[network]?.chainId,
        from: initiator,
        to: data ? token?.tokenIdentifier : tx.receiver,
        ...(data ? {} : { value: tx.amount * 10 ** 18 }),
        ...(data ? { data } : {}),
      })
    } else {
      txResponse = await metamaskAdapter.sendTransaction({
        from: initiator,
        to: data ? token?.tokenIdentifier : tx.receiver,
        value: data ? 0 : intToHex(tx.amount * 10 ** 18),
        ...(data ? { data } : {}),
        chainId: networksData?.[chain]?.[network]?.chainId,
      })
    }

    tx.tx = txResponse
    tx.status = 'pending'

    if (transactions.length >= 10 && txCount % 10 === 0) {
      const newStatusTransactions = transactions.slice(txCount - 10, txCount)
      updateTransactions(newStatusTransactions)
    }
    txCount += 1
  }

  //Update the last transactions that are fewer than 10
  if (transactions.length % 10 !== 0) {
    const newStatusTransactions = transactions.slice(-(transactions.length % 10))
    updateTransactions(newStatusTransactions)
  }
}

export const validateTransactions = async (
  transactions: txType[],
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
) => {
  try {
    switch (chain) {
      case 'multiversx':
        for (const tx of transactions) {
          tx.status = await checkTransaction(tx.tx as string)
        }
        break
      case 'ethereum':
      case 'polygon':
      case 'bnb':
        const web3 = new Web3(
          new Web3.providers.HttpProvider(networksData[chain][network].rpc[rpcRequestCount]),
        )
        for (const tx of transactions) {
          let receipt
          try {
            receipt = await web3.eth.getTransactionReceipt(tx.tx as string)
            incrementRpcCount()
          } catch (error) {
            console.error(error)
          }
          if (receipt && Number(receipt.status)) {
            tx.status = 'completed'
          } else {
            tx.status = 'failed'
          }
        }
        break
    }

    //update transactions in db
    await updateTransactions(transactions)
  } catch (error) {
    console.error(error)
    return error
  }
}

export const getMvxBalance = async (address: string): Promise<any> => {
  try {
    const multiversxApi =
      process.env.REACT_APP_ENVIRONMENT === 'production'
        ? 'https://api.multiversx.com	'
        : 'https://devnet-api.multiversx.com'
    const response = await axios.get(`${multiversxApi}/accounts/${address}`)

    return response?.data?.balance / 1000000000000000000
  } catch (error) {
    console.error(error)
    return error
  }
}

export const getEvmBalance = async (
  address: string,
  chain: keyof chainsDataType,
  network: networkType,
  rpcRequestCount: number,
  incrementRpcCount: () => void,
): Promise<any> => {
  try {
    const web3 = new Web3(
      new Web3.providers.HttpProvider(networksData?.[chain]?.[network]?.rpc[rpcRequestCount]),
    )
    const balance = await web3.eth.getBalance(address)
    incrementRpcCount()
    return Number(balance) / 10 ** 18
  } catch (error) {
    console.error(error)
    return error
  }
}

export const getEsdtBalance = async (address: string, tokenIdentifier: string) => {
  try {
    const multiversxApi =
      process.env.REACT_APP_ENVIRONMENT === 'production'
        ? 'https://api.multiversx.com	'
        : 'https://devnet-api.multiversx.com'
    const userTokens = await axios.get(`${multiversxApi}/accounts/${address}/tokens?size=150`)

    const selectedEsdt = userTokens?.data?.find(
      (token: any) => token.identifier === tokenIdentifier,
    )

    return selectedEsdt?.balance / 10 ** selectedEsdt?.decimals
  } catch (error) {
    console.error(error)
    return error
  }
}
